import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../api";
import { toast } from "sonner";
import { PlayCircle, Lock, Clock, ArrowLeft, CheckCircle2, Circle, Trophy } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCourse(id)
      .then((c) => {
        setCourse(c);
        const first = (c.modules || []).flatMap((m) => m.lessons)[0];
        if (c.access && first) setActive(first);
      })
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
    api.myProgress().then((list) => setCompleted(list.find((x) => x.courseId === id)?.completed || [])).catch(() => {});
  }, [id]);

  if (loading) return <div className="pt-32 text-center text-white/50">Загрузка...</div>;
  if (!course) return <div className="pt-32 text-center">Курс не найден</div>;

  const access = course.access;
  const modules = course.modules || [];
  const allLessons = modules.flatMap((m) => m.lessons);
  const total = allLessons.length;
  const doneCount = allLessons.filter((l) => completed.includes(l.id)).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const activeDone = active && completed.includes(active.id);

  const toggleComplete = async (lesson, value) => {
    const prev = completed;
    const next = value ? [...new Set([...completed, lesson.id])] : completed.filter((x) => x !== lesson.id);
    setCompleted(next);
    try {
      await api.setProgress({ courseId: id, lessonId: lesson.id, completed: value });
      if (value && next.length === total) toast.success("Поздравляем! Курс пройден 🎉");
    } catch { setCompleted(prev); toast.error("Не удалось сохранить прогресс"); }
  };

  return (
    <main className="pt-[72px] min-h-screen relative overflow-hidden">
      <div className="absolute -top-10 -left-20 h-72 w-72 rounded-full bg-[#ff3798]/15 blob" />
      <div className="max-w-7xl mx-auto px-5 py-10 relative z-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> Назад в кабинет</button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* MAIN */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="rounded-full bg-[#d6ff71] text-black px-3 py-1 text-xs font-semibold">{course.level}</span>
              <span className="flex items-center gap-1.5 text-sm text-white/50"><Clock size={15} />{course.duration}</span>
            </div>
            <h1 className="mt-4 text-3xl font-extrabold">{course.title}</h1>
            <p className="text-[#ff9ccb] mt-1">{course.subtitle}</p>
            <p className="text-white/60 mt-4">{course.description}</p>

            {access && active ? (
              <div className="mt-8">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="text-xl font-bold text-[#d6ff71]">{active.title}</h2>
                  <button onClick={() => toggleComplete(active, !activeDone)} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${activeDone ? "bg-[#d6ff71] text-black hover:bg-[#c4f24d]" : "border border-[#d6ff71]/50 text-[#d6ff71] hover:bg-[#d6ff71] hover:text-black"}`}>{activeDone ? <><CheckCircle2 size={16} /> Пройден</> : <><Circle size={16} /> Отметить пройденным</>}</button>
                </div>
                {active.description && <p className="mt-3 text-white/60 whitespace-pre-line">{active.description}</p>}

                {/* PHOTOS under the lesson */}
                {(active.photos || []).length > 0 && (
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {active.photos.map((p, i) => (
                      <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-white/10 hover:border-[#ff3798]/50 transition-colors">
                        <img src={fileUrl(p)} alt="" className="w-full h-32 object-cover" />
                      </a>
                    ))}
                  </div>
                )}

                {/* VIDEO at the BOTTOM */}
                <div className="mt-6 rounded-3xl overflow-hidden border border-white/10 bg-black">
                  {active.videoType === "file" && active.videoUrl ? (
                    <video key={active.id} src={fileUrl(active.videoUrl)} poster={active.cover ? fileUrl(active.cover) : undefined} controls playsInline className="w-full aspect-video bg-black" />
                  ) : active.youtubeId ? (
                    <div className="aspect-video"><iframe key={active.id} className="w-full h-full" src={`https://www.youtube.com/embed/${active.youtubeId}`} title={active.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                  ) : (
                    <div className="aspect-video flex items-center justify-center text-white/40 text-sm">Видео для этого урока пока не добавлено</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-8 rounded-3xl overflow-hidden border border-white/10 relative aspect-video flex items-center justify-center">
                <img src={course.cover} alt={course.title} className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale" />
                <div className="relative text-center px-6">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#ff3798]/20 border border-[#ff3798]/40 flex items-center justify-center text-[#ff3798]"><Lock size={28} /></div>
                  <h3 className="mt-4 text-xl font-bold">Доступ закрыт</h3>
                  <p className="text-white/60 mt-2 max-w-sm">Чтобы смотреть уроки этого курса, дождитесь открытия доступа администратором.</p>
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div>
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 lg:sticky lg:top-24">
              {access && (
                <div className="mb-5">
                  <div className="flex items-center justify-between text-sm mb-2"><span className="font-semibold flex items-center gap-1.5"><Trophy size={15} className="text-[#d6ff71]" /> Прогресс</span><span className="text-[#d6ff71] font-bold">{doneCount}/{total} · {pct}%</span></div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#ff3798] to-[#d6ff71] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
                </div>
              )}
              <h3 className="font-bold mb-4">Программа курса</h3>
              <div className="space-y-4">
                {modules.map((mod, mi) => (
                  <div key={mod.id || mi}>
                    <div className="text-xs uppercase tracking-wide text-white/40 mb-2">{mod.title}</div>
                    <div className="space-y-2">
                      {(mod.lessons || []).map((l, i) => {
                        const isActive = active?.id === l.id;
                        const isDone = completed.includes(l.id);
                        return (
                          <button key={l.id} onClick={() => access && setActive(l)} disabled={!access} className={`w-full text-left rounded-2xl border p-3.5 transition-all flex items-center gap-3 ${isActive ? "border-[#d6ff71] bg-[#d6ff71]/10" : access ? "border-white/10 hover:border-white/25 bg-black/20" : "border-white/5 bg-black/10 cursor-not-allowed"}`}>
                            <div className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${isDone ? "bg-[#d6ff71] text-black" : access ? "bg-[#ff3798]/20 text-[#ff3798]" : "bg-white/5 text-white/30"}`}>{!access ? <Lock size={15} /> : isDone ? <CheckCircle2 size={18} /> : isActive ? <PlayCircle size={18} /> : <span className="text-sm font-semibold">{i + 1}</span>}</div>
                            <div className="min-w-0 flex-1"><div className={`text-sm font-medium truncate ${access ? "" : "text-white/40"}`}>{l.title}</div><div className="text-xs text-white/40">{l.duration}</div></div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {!access && (<div className="mt-4 rounded-2xl bg-[#ff3798]/10 border border-[#ff3798]/30 p-4 text-sm text-center text-white/70"><Lock className="mx-auto mb-2 text-[#ff3798]" size={20} />Уроки откроются после открытия доступа</div>)}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
